import type { Action, MatchEvent } from '../engine';
import { LESSONS, type Lesson } from '../tutorial/lessons';
import { buildPosition } from '../tutorial/position';
import type { AiClient } from './aiClient';
import { MatchHost } from './matchHost';
import { loadTutorial, saveTutorial, type TutorialProgress } from './tutorialStore';

/**
 * The tutorial in progress: which lesson is open, the match host playing its position, and how
 * the player is doing in it. A small external store for React (subscribe / getSnapshot); the
 * screen opens it on mount and closes it on unmount.
 */

export type LessonStatus =
  | { readonly kind: 'playing' }
  /** The lesson is solved and the board is still; the verdict comes once the move has landed. */
  | { readonly kind: 'settling' }
  | { readonly kind: 'done'; readonly message: 'done' | 'donePlus'; readonly score: number }
  | { readonly kind: 'retry' };

export interface TutorialSnapshot {
  /** Index of the open lesson; LESSONS.length is the closing page. */
  readonly index: number;
  readonly lesson: Lesson | null;
  readonly host: MatchHost | null;
  readonly status: LessonStatus;
  /** Counts the hosts opened, so each attempt gets a fresh screen. */
  readonly attempt: number;
  readonly progress: TutorialProgress;
}

export interface TutorialSessionOptions {
  readonly ai: AiClient;
  readonly delay?: (action?: Action) => number;
  /**
   * How long to wait before a lesson's outcome is shown, given the events of the move that
   * decided it: long enough for the move to be seen landing (a drawn card, a card on the pile).
   */
  readonly outcomeDelay?: (events: readonly MatchEvent[]) => number;
  /** Names shown for the two sides. */
  readonly names: { readonly you: string; readonly north: string };
}

const PLAYING: LessonStatus = { kind: 'playing' };

export class TutorialSession {
  private snapshot: TutorialSnapshot;
  private readonly listeners = new Set<() => void>();

  constructor(private readonly options: TutorialSessionOptions) {
    const progress = loadTutorial();
    const index = progress.completed ? 0 : Math.min(progress.lesson, LESSONS.length);
    this.snapshot = {
      index,
      lesson: LESSONS[index] ?? null,
      host: null,
      status: PLAYING,
      attempt: 0,
      progress,
    };
  }

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): TutorialSnapshot => this.snapshot;

  /** Starts the current lesson (again): a fresh position and a fresh host. */
  open(): void {
    this.snapshot.host?.dispose();
    const lesson = LESSONS[this.snapshot.index] ?? null;
    this.update({
      lesson,
      host: lesson ? this.hostFor(lesson) : null,
      status: PLAYING,
      attempt: this.snapshot.attempt + 1,
    });
  }

  /** Stops the lesson being played. */
  close(): void {
    this.snapshot.host?.dispose();
  }

  restart(): void {
    this.open();
  }

  /** Opens another lesson, or the closing page (LESSONS.length). */
  goTo(index: number): void {
    const i = Math.max(0, Math.min(index, LESSONS.length));
    const p = this.snapshot.progress;
    this.save({
      ...p,
      lesson: i,
      completed: p.completed || i === LESSONS.length,
    });
    this.update({ index: i });
    this.open();
  }

  next(): void {
    this.goTo(this.snapshot.index + 1);
  }

  previous(): void {
    this.goTo(this.snapshot.index - 1);
  }

  private hostFor(lesson: Lesson): MatchHost {
    const state = buildPosition(lesson.position);
    const history: MatchEvent[] = [];
    let host: MatchHost | null = null;
    host = new MatchHost(
      {
        config: state.config,
        seed: `tutorial-${lesson.id}`,
        controllers: {
          [state.seats.S]: { kind: 'human', name: this.options.names.you },
          [state.seats.N]: { kind: 'ai', name: this.options.names.north, difficulty: 'easy' },
        },
      },
      {
        ai: this.options.ai,
        initialState: state,
        ...(this.options.delay ? { delay: this.options.delay } : {}),
        onAction: (before, action, after, events) => {
          if (this.snapshot.host !== host || this.snapshot.status.kind !== 'playing') return;
          history.push(...events);
          if (action.side !== 'S') return;
          const outcome = lesson.evaluate({ before, action, after, events }, history);
          if (outcome.kind === 'continue') return;
          // The lesson is over: nothing else moves on the board.
          host?.setPaused(true);
          if (outcome.kind === 'done') {
            const p = this.snapshot.progress;
            this.save({
              ...p,
              lesson: Math.max(p.lesson, this.snapshot.index + 1),
              solved: p.solved.includes(lesson.id) ? p.solved : [...p.solved, lesson.id],
            });
          }
          if (outcome.kind === 'retry') {
            this.update({ status: { kind: 'retry' } });
            return;
          }
          const done: LessonStatus = {
            kind: 'done',
            message: outcome.message,
            score: outcome.score,
          };
          // Praise comes once the move has been seen landing, unless the lesson changed meanwhile.
          const wait = this.options.outcomeDelay?.(events) ?? 0;
          if (wait <= 0) {
            this.update({ status: done });
            return;
          }
          this.update({ status: { kind: 'settling' } });
          setTimeout(() => {
            if (this.snapshot.host === host && this.snapshot.status.kind === 'settling')
              this.update({ status: done });
          }, wait);
        },
      },
    );
    return host;
  }

  private save(progress: TutorialProgress): void {
    saveTutorial(progress);
    this.update({ progress });
  }

  private update(patch: Partial<TutorialSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const l of this.listeners) l();
  }
}
